import React, {useState, useEffect, useRef} from 'react';
import {
    Box,
    Typography,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    List,
    ListItemButton,
    ListItemText,
    Alert,
    CircularProgress,
    Chip,
    TextField,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StorageIcon from '@mui/icons-material/Storage';
import {toast} from 'react-toastify';
import {adminApi} from 'utils/api';

const styles = {
    root: {
        display: 'flex',
        gap: 2,
        height: 'calc(100vh - 280px)',
        minHeight: 500,
    },
    sidebar: {
        width: 220,
        minWidth: 220,
        bgcolor: 'background.default',
        borderRadius: 1,
        overflow: 'auto',
    },
    sidebarTitle: {
        p: 1.5,
        pb: 1,
        fontSize: '0.75rem',
        fontWeight: 600,
        color: 'text.secondary',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
    },
    main: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        overflow: 'hidden',
    },
    editorContainer: {
        bgcolor: 'background.default',
        borderRadius: 1,
        p: 2,
    },
    editorHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 1,
    },
    sqlInput: {
        '& .MuiInputBase-root': {
            fontFamily: 'monospace',
            fontSize: '0.8125rem',
            bgcolor: '#0d1117',
        },
    },
    resultContainer: {
        flex: 1,
        overflow: 'auto',
        bgcolor: 'background.default',
        borderRadius: 1,
    },
    resultHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        p: 1.5,
        pb: 1,
    },
    resultMeta: {
        fontSize: '0.75rem',
        color: 'text.secondary',
    },
    tableCell: {
        fontFamily: 'monospace',
        fontSize: '0.75rem',
        whiteSpace: 'nowrap',
        maxWidth: 300,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    tableCellHeader: {
        fontFamily: 'monospace',
        fontSize: '0.75rem',
        fontWeight: 600,
    },
};

export default function DbConsoleTab() {
    const [tables, setTables] = useState([]);
    const [selectedTable, setSelectedTable] = useState(null);
    const [sql, setSql] = useState('');
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loadingTables, setLoadingTables] = useState(true);
    const inputRef = useRef(null);

    useEffect(() => {
        loadTables();
    }, []);

    const loadTables = async () => {
        try {
            const {data} = await adminApi.getTables();
            setTables(data.tables || []);
        } catch (err) {
            toast.error('테이블 목록 로드 실패');
        } finally {
            setLoadingTables(false);
        }
    };

    const handleSelectTable = async (tableName) => {
        setSelectedTable(tableName);
        setSql(`SELECT * FROM ${tableName} LIMIT 100`);
        await executeQuery(`SELECT * FROM ${tableName} LIMIT 100`);
    };

    const executeQuery = async (queryStr) => {
        const query = queryStr || sql;
        if (!query.trim()) {
            toast.error('SQL 쿼리를 입력하세요.');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const {data} = await adminApi.executeQuery(query);
            setResult(data);
        } catch (err) {
            const errMsg = err.response?.data?.error || '쿼리 실행 실패';
            setError(errMsg);
            setResult(null);
        } finally {
            setLoading(false);
        }
    };

    const handleKeyDown = (e) => {
        // Ctrl+Enter or Cmd+Enter to execute
        if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
            e.preventDefault();
            executeQuery();
        }
    };

    const renderValue = (value) => {
        if (value === null || value === undefined) {
            return <span style={{color: '#64748b', fontStyle: 'italic'}}>NULL</span>;
        }
        if (typeof value === 'string' && value.length > 100) {
            return value.substring(0, 100) + '...';
        }
        return String(value);
    };

    return (
        <Box>
            <Typography variant="h2" sx={{mb: 2}}>
                DB 콘솔
            </Typography>

            <Box sx={styles.root}>
                {/* Sidebar - Table List */}
                <Paper sx={styles.sidebar} variant="outlined">
                    <Typography sx={styles.sidebarTitle}>
                        <StorageIcon sx={{fontSize: 14, mr: 0.5, verticalAlign: 'middle'}}/>
                        테이블
                    </Typography>
                    {loadingTables ? (
                        <Box sx={{display: 'flex', justifyContent: 'center', py: 2}}>
                            <CircularProgress size={20}/>
                        </Box>
                    ) : (
                        <List dense disablePadding>
                            {tables.map((t) => (
                                <ListItemButton
                                    key={t.name}
                                    selected={selectedTable === t.name}
                                    onClick={() => handleSelectTable(t.name)}
                                    sx={{py: 0.5}}
                                >
                                    <ListItemText
                                        primary={t.name}
                                        secondary={`${t.rowCount}행`}
                                        primaryTypographyProps={{
                                            fontSize: '0.8125rem',
                                            fontFamily: 'monospace',
                                        }}
                                        secondaryTypographyProps={{
                                            fontSize: '0.6875rem',
                                        }}
                                    />
                                </ListItemButton>
                            ))}
                        </List>
                    )}
                </Paper>

                {/* Main - Query Editor + Results */}
                <Box sx={styles.main}>
                    {/* SQL Editor */}
                    <Paper sx={styles.editorContainer} variant="outlined">
                        <Box sx={styles.editorHeader}>
                            <Typography sx={{fontSize: '0.75rem', color: 'text.secondary'}}>
                                SQL 쿼리 (Ctrl+Enter로 실행)
                            </Typography>
                            <Button
                                variant="contained"
                                size="small"
                                startIcon={<PlayArrowIcon/>}
                                onClick={() => executeQuery()}
                                disabled={loading || !sql.trim()}
                            >
                                {loading ? '실행 중...' : '실행'}
                            </Button>
                        </Box>
                        <TextField
                            ref={inputRef}
                            value={sql}
                            onChange={(e) => setSql(e.target.value)}
                            onKeyDown={handleKeyDown}
                            multiline
                            minRows={3}
                            maxRows={6}
                            fullWidth
                            placeholder="SELECT * FROM users LIMIT 10"
                            sx={styles.sqlInput}
                            size="small"
                        />
                    </Paper>

                    {/* Error */}
                    {error && (
                        <Alert severity="error" sx={{fontSize: '0.8125rem'}}>
                            {error}
                        </Alert>
                    )}

                    {/* Results */}
                    {result && (
                        <Paper sx={styles.resultContainer} variant="outlined">
                            <Box sx={styles.resultHeader}>
                                <Typography sx={{fontSize: '0.8125rem', fontWeight: 600}}>
                                    결과
                                </Typography>
                                <Box sx={{display: 'flex', gap: 1}}>
                                    <Chip
                                        label={`${result.rowCount}행`}
                                        size="small"
                                        variant="outlined"
                                    />
                                    <Chip
                                        label={`${result.elapsed}ms`}
                                        size="small"
                                        variant="outlined"
                                        color="primary"
                                    />
                                </Box>
                            </Box>

                            {result.rows.length === 0 ? (
                                <Typography sx={{p: 2, color: 'text.secondary', fontSize: '0.8125rem'}}>
                                    결과가 없습니다.
                                </Typography>
                            ) : (
                                <TableContainer sx={{maxHeight: 'calc(100% - 48px)'}}>
                                    <Table stickyHeader size="small">
                                        <TableHead>
                                            <TableRow>
                                                {result.columns.map((col) => (
                                                    <TableCell key={col} sx={styles.tableCellHeader}>
                                                        {col}
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {result.rows.map((row, idx) => (
                                                <TableRow key={idx} hover>
                                                    {result.columns.map((col) => (
                                                        <TableCell key={col} sx={styles.tableCell}>
                                                            {renderValue(row[col])}
                                                        </TableCell>
                                                    ))}
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </Paper>
                    )}
                </Box>
            </Box>
        </Box>
    );
}
